import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();
  
  /** @type {AbortController | null} */
  static #documentAbortController = null;

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    this.addEventListener('focusin', this.#handleFocusIn, {
      signal: this.#abortController.signal,
      capture: true,
    });
    
    this.addEventListener('focusout', this.#handleFocusOut, {
      signal: this.#abortController.signal,
      capture: true,
    });

    this.addEventListener('keydown', this.#handleKeyDown, {
      signal: this.#abortController.signal,
    });

    // Set up document-level Escape listener (only once for all instances)
    if (!HeaderMenu.#documentAbortController) {
      HeaderMenu.#documentAbortController = new AbortController();
      document.addEventListener('keydown', HeaderMenu.#handleDocumentKeyDown, {
        signal: HeaderMenu.#documentAbortController.signal,
        capture: true,
      });
    }

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };
  
  /**
   * Track if we're currently activating an item to prevent duplicate activations
   * @type {boolean}
   */
  #isActivating = false;
  /**
   * @type {HTMLElement | null}
   */
  #activatingItem = null;

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    const overflowList = this.refs.overflowMenu;
    if (!overflowList || Array.isArray(overflowList)) return null;
    return /** @type {HTMLElement | null} */ (overflowList.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    const overflowList = this.refs.overflowMenu;
    if (!overflowList || Array.isArray(overflowList)) return false;
    return overflowList.matches(':hover') ?? false;
  }


  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (event instanceof FocusEvent) {
      const activeElement = document.activeElement;
      if (activeElement instanceof HTMLElement && activeElement.matches('[data-toggle-button]')) {
        return;
      }
      
      const checkElement = (/** @type {EventTarget | null} */ element) => {
        if (!(element instanceof HTMLElement)) return false;
        const menuButton = element.closest('button[role="menuitem"][aria-haspopup="true"]');
        return menuButton instanceof HTMLElement;
      };
      
      if (checkElement(document.activeElement) || checkElement(event.target)) {
        return;
      }
    }
    
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent | { target: Element }} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    
    if (target instanceof HTMLElement && target.matches('[data-toggle-button]')) {
      return;
    }
    if (target instanceof HTMLElement && target.closest('[data-toggle-button]')) {
      return;
    }

    let item = findMenuItem(target);

    if (!item || item == this.#state.activeItem) return;
    
    if (this.#isActivating && this.#activatingItem === item) {
      return;
    }
    
    this.#isActivating = true;
    this.#activatingItem = item;

    const isDefaultSlot = target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;
    if (previouslyActiveItem && previouslyActiveItem !== item) {
      previouslyActiveItem.ariaExpanded = 'false';
      const prevListItem = previouslyActiveItem.closest('.menu-list__list-item');
      const prevToggleButton = prevListItem?.querySelector('[data-toggle-button]');
      if (prevToggleButton instanceof HTMLElement) {
        prevToggleButton.setAttribute('aria-expanded', 'false');
      }
      const prevSubmenu = findSubmenu(previouslyActiveItem);
      if (prevSubmenu) {
        delete prevSubmenu.dataset.active;
        prevSubmenu.style.visibility = 'hidden';
      }
      this.#state.activeItem = null;
    }

    this.#state.activeItem = item;
    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      this.ariaExpanded = 'true';
      item.ariaExpanded = 'true';
      const listItem = item.closest('.menu-list__list-item');
      const toggleButton = listItem?.querySelector('[data-toggle-button]');
      if (toggleButton instanceof HTMLElement) {
        toggleButton.setAttribute('aria-expanded', 'true');
      }
    }

    if (submenu) {
      submenu.dataset.active = '';
      submenu.style.visibility = '';
      
      void submenu.offsetHeight;
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
    
    requestAnimationFrame(() => {
      this.#isActivating = false;
      this.#activatingItem = null;
    });
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Close the menu if it's open (public method for external use)
   */
  closeMenu() {
    const activeItem = this.#state.activeItem;
    if (!activeItem) return;

    const menuButton = activeItem instanceof HTMLElement && activeItem.matches('button[role="menuitem"][aria-haspopup="true"]')
      ? activeItem
      : activeItem.closest('button[role="menuitem"][aria-haspopup="true"]');
    
    if (!(menuButton instanceof HTMLElement)) return;

    const listItem = menuButton.closest('.menu-list__list-item');
    const toggleButton = listItem?.querySelector('[data-toggle-button]');

    this.#deactivate(activeItem, true);
    menuButton.setAttribute('aria-expanded', 'false');
    
    if (toggleButton instanceof HTMLElement) {
      toggleButton.setAttribute('aria-expanded', 'false');
      toggleButton.classList.add('visually-hidden');
      toggleButton.setAttribute('tabindex', '-1');
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {boolean} [force=false] - Force deactivation even if hovered
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    if (!item || item != this.#state.activeItem) return;
    if (!force && this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    
    if (submenu) {
      this.ariaExpanded = 'false';
      item.ariaExpanded = 'false';
      const listItem = item.closest('.menu-list__list-item');
      const toggleButton = listItem?.querySelector('[data-toggle-button]');
      if (toggleButton instanceof HTMLElement) {
        toggleButton.setAttribute('aria-expanded', 'false');
      }
    }
    
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
      if (submenu) {
        delete submenu.dataset.active;
      }
    }, Math.max(0, this.animationDelay - 150));
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Static handler for document-level Escape key to close all open menus
   * @param {KeyboardEvent} event
   */
  static #handleDocumentKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    
    // Find all header-menu instances and close any that are open
    const allMenus = document.querySelectorAll('header-menu');
    allMenus.forEach((menu) => {
      if (menu instanceof HeaderMenu) {
        menu.closeMenu();
      }
    });
  };

  /**
   * Handle global keydown events (e.g., Escape to close submenu, Enter to close when hovered)
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    const activeItem = this.#state.activeItem;
    if (!activeItem) return;

    if (event.key === 'Escape') {
      const menuButton = activeItem instanceof HTMLElement && activeItem.matches('button[role="menuitem"][aria-haspopup="true"]')
        ? activeItem
        : activeItem.closest('button[role="menuitem"][aria-haspopup="true"]');
      
      if (!(menuButton instanceof HTMLElement)) return;

      const listItem = menuButton.closest('.menu-list__list-item');
      const toggleButton = listItem?.querySelector('[data-toggle-button]');

      this.#deactivate(activeItem, true);
      menuButton.setAttribute('aria-expanded', 'false');
      
      if (toggleButton instanceof HTMLElement) {
        toggleButton.setAttribute('aria-expanded', 'false');
        toggleButton.classList.add('visually-hidden');
        toggleButton.setAttribute('tabindex', '-1');
      }

      event.preventDefault();
      event.stopPropagation();
      requestAnimationFrame(() => {
        menuButton.focus();
      });
      return;
    }

    if (event.key === 'Enter') {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      const menuButton = target.closest('button[role="menuitem"][aria-haspopup="true"]');
      if (menuButton instanceof HTMLElement && menuButton === activeItem) {
        const listItem = menuButton.closest('.menu-list__list-item');
        const toggleButton = listItem?.querySelector('[data-toggle-button]');

        this.#deactivate(activeItem, true);
        menuButton.setAttribute('aria-expanded', 'false');
        
        if (toggleButton instanceof HTMLElement) {
          toggleButton.setAttribute('aria-expanded', 'false');
        }

        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const submenu = activeItem.closest('.menu-list__list-item')?.querySelector('[ref="submenu[]"]');
      if (submenu && submenu.contains(target)) {
        const menuButton = activeItem instanceof HTMLElement && activeItem.matches('button[role="menuitem"][aria-haspopup="true"]')
          ? activeItem
          : activeItem.closest('button[role="menuitem"][aria-haspopup="true"]');
        
        if (menuButton instanceof HTMLElement) {
          const listItem = menuButton.closest('.menu-list__list-item');
          const toggleButton = listItem?.querySelector('[data-toggle-button]');

          this.#deactivate(activeItem, true);
          menuButton.setAttribute('aria-expanded', 'false');
          
          if (toggleButton instanceof HTMLElement) {
            toggleButton.setAttribute('aria-expanded', 'false');
          }

          event.preventDefault();
          event.stopPropagation();
          requestAnimationFrame(() => {
            menuButton.focus();
          });
        }
      }
    }
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };


  /**
   * Handle focus events and set up keyboard listeners for buttons with submenus
   * @param {FocusEvent} event
   */
  #handleFocusIn = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    
    this.querySelectorAll('[data-toggle-button]').forEach((btn) => {
      if (btn instanceof HTMLElement) {
        btn.classList.add('visually-hidden');
        btn.setAttribute('tabindex', '-1');
      }
    });
    
    const toggleButton = target.closest('[data-toggle-button]');
    if (toggleButton instanceof HTMLElement) {
      toggleButton.classList.remove('visually-hidden');
      toggleButton.setAttribute('tabindex', '0');
      
      if (toggleButton.dataset.hasKeyboardHandler !== 'true') {
        toggleButton.dataset.hasKeyboardHandler = 'true';
        this.#setupToggleButtonKeyboardHandler(toggleButton);
      }
      return;
    }
    
    const menuButton = target.closest('button[role="menuitem"][aria-haspopup="true"]');
    if (menuButton instanceof HTMLElement) {
      const toggleBtn = menuButton.nextElementSibling;
      if (toggleBtn instanceof HTMLElement && toggleBtn.matches('[data-toggle-button]')) {
        toggleBtn.classList.remove('visually-hidden');
        toggleBtn.setAttribute('tabindex', '0');
      }
      
      if (menuButton.dataset.hasKeyboardHandler !== 'true') {
        menuButton.dataset.hasKeyboardHandler = 'true';
        this.#setupMenuButtonKeyboardHandler(menuButton);
      }
      return;
    }
  };
  
  /**
   * Handle focusout events to hide toggle buttons when focus leaves the menu item
   * @param {FocusEvent} event
   */
  #handleFocusOut = (event) => {
    const relatedTarget = event.relatedTarget;
    const target = event.target;
    
    if (!(target instanceof HTMLElement)) return;
    
    const listItem = target.closest('.menu-list__list-item');
    if (!listItem) return;
    
      if (relatedTarget instanceof HTMLElement && listItem.contains(relatedTarget)) {
      return;
    }
    
    const toggleButton = listItem.querySelector('[data-toggle-button]');
    if (toggleButton instanceof HTMLElement) {
      toggleButton.classList.add('visually-hidden');
      toggleButton.setAttribute('tabindex', '-1');
    }
  };
  
  /**
   * Set up keyboard handler for menu button
   * @param {HTMLElement} menuButton
   */
  #setupMenuButtonKeyboardHandler(menuButton) {
    const handleKeyDown = (/** @type {KeyboardEvent} */ keyEvent) => {
      if (keyEvent.key === 'ArrowRight' || keyEvent.key === 'ArrowDown') {
        const toggleButton = menuButton.nextElementSibling;
        if (toggleButton instanceof HTMLElement && toggleButton.matches('[data-toggle-button]')) {
          keyEvent.preventDefault();
          toggleButton.classList.remove('visually-hidden');
          toggleButton.setAttribute('tabindex', '0');
          toggleButton.focus();
        }
      }
    };
    
    menuButton.addEventListener('keydown', handleKeyDown, {
      signal: this.#abortController.signal,
    });
  }
  
  /**
   * Set up keyboard handler for toggle button
   * @param {HTMLElement} toggleButton
   */
  #setupToggleButtonKeyboardHandler(toggleButton) {
    const handleKeyDown = (/** @type {KeyboardEvent} */ keyEvent) => {
      const relevantKeys = ['Enter', ' ', 'Escape', 'ArrowLeft', 'ArrowUp'];
      if (!relevantKeys.includes(keyEvent.key)) {
        return;
      }
      
      const menuButton = toggleButton.previousElementSibling;
      if (!(menuButton instanceof HTMLElement) || !menuButton.matches('button[role="menuitem"][aria-haspopup="true"]')) {
        console.warn('Toggle button: Could not find associated menu button');
        return;
      }
      
      const item = menuButton.getAttribute('ref') === 'menuitem' 
        ? menuButton 
        : findMenuItem(menuButton);
      if (!item) {
        console.warn('Toggle button: Could not find menu item, menuButton ref:', menuButton.getAttribute('ref'));
        return;
      }

      const isDefaultSlot = menuButton.slot === '';
      let submenu = findSubmenu(item);
      
      if (!submenu && !isDefaultSlot) {
        submenu = this.overflowMenu;
      }
      
      if (!submenu) {
        console.warn('Toggle button: Could not find submenu');
        return;
      }

      const isExpanded = toggleButton.getAttribute('aria-expanded') === 'true';
      const submenuItems = submenu.querySelectorAll('a[href], button[type="button"]');
      const firstSubmenuItem = submenuItems[0];

      switch (keyEvent.key) {
        case 'Enter':
        case ' ': // Space
          keyEvent.preventDefault();
          keyEvent.stopPropagation();
          
          console.log('Toggle button: Enter/Space pressed, isExpanded:', isExpanded);
          
          if (isExpanded) {
            this.#deactivate(item, true);
            toggleButton.setAttribute('aria-expanded', 'false');
            menuButton.setAttribute('aria-expanded', 'false');
          } else {
            console.log('Toggle button: Opening submenu');
            this.#activateHandler({ target: menuButton });
            toggleButton.setAttribute('aria-expanded', 'true');
            menuButton.setAttribute('aria-expanded', 'true');
            if (firstSubmenuItem instanceof HTMLElement) {
              requestAnimationFrame(() => {
                firstSubmenuItem.focus();
              });
            }
          }
          break;

        case 'Escape':
          if (isExpanded) {
            keyEvent.preventDefault();
            keyEvent.stopPropagation();
            
            this.#deactivate(item, true);
            toggleButton.setAttribute('aria-expanded', 'false');
            menuButton.setAttribute('aria-expanded', 'false');
            toggleButton.classList.add('visually-hidden');
            toggleButton.setAttribute('tabindex', '-1');
            requestAnimationFrame(() => {
              menuButton.focus();
            });
          }
          break;

        case 'ArrowLeft':
        case 'ArrowUp':
          keyEvent.preventDefault();
          keyEvent.stopPropagation();
          menuButton.focus();
          break;
      }
    };
    
    toggleButton.addEventListener('keydown', handleKeyDown, {
      signal: this.#abortController.signal,
    });
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
